import { useThemeContext } from "@kalyzee/kalyzee-ui";
import { useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { AddEventListernerResult, CyclingQRCodeData, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { Chatroom, ChatroomStatus, Meeting, MeetingSession, MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PageSlaveMode } from "../../components/navigation/PageContext";
import { SessionMode } from "../../contexts/appStore/hooks";
import { useMeetingUtilsContext } from "../../contexts/meetingUtils/context";
import { useMessagingBridgeManagerMasterContext, useMessagingBridgeManagerSlaveContext } from "../../contexts/messageBridgeManager";
import { MessagingBridgeManagerMasterClient } from "../../helpers/bridge/master";
import { useVideoCarousel } from "./videoCarousel";
import { SlaveChatAdapter } from "../../helpers/chat";
import { ClientContainersIteration } from "../../helpers/bridge/common";

export const useMeetingMasterMessagingBridge = () => {
  const bridge = useMessagingBridgeManagerMasterContext();
  const bridgeRef = useRefAutoUpdate(bridge);

  const unmountedRef = useRef(false);

  const { containerAPI } = useMeetingUtilsContext();

  const [meeting, meetingRef] = useMeetingSelector(containerAPI, "meeting");
  const [session, sessionRef] = useMeetingSelector(containerAPI, "session");
  const [sessions, sessionsRef] = useMeetingSelector(containerAPI, "sessions");
  const [chatrooms, chatroomsRef] = useMeetingSelector(containerAPI, "chatrooms");
  
  const [chatAdapters] = useMeetingSelector(containerAPI, "chatAdapters");
  const [mainChatroom] = useMeetingSelector(containerAPI, "chatrooms", chatrooms => chatrooms?.[0]);

  const chatAdapter = useMemo(() => {
    if (!chatAdapters || !mainChatroom) return undefined;
    return chatAdapters[mainChatroom.id];
  }, [chatAdapters, mainChatroom])
  const chatAdapterRef = useRefAutoUpdate(chatAdapter);

  const themeCtx = useThemeContext();
  
  const getMessagingBridgeClients = useCallback((): MessagingBridgeManagerMasterClient<SessionMode.SLAVE, PageSlaveMode.MEETING>[] => {
    return bridgeRef.current?.getActiveClients(true).filter((c) => c.data?.mode === SessionMode.SLAVE && c.data?.page === PageSlaveMode.MEETING) ?? [];
  }, []);

  const broadcastVideoCarousel = useCallback(
    (data: ClientContainersIteration, delay: number, id: string) => {
      if (unmountedRef.current) return;

      const bridge = bridgeRef.current;
      bridge?.postDisplayCarousel(data, delay, id);
    }, 
    [],
  );

  const broadcastMeeting = useCallback(
    debounce(
      () => {
        // the debounce can cause this fn to be called after unmount
        // which overrides the undefined posting on unmount
        if (unmountedRef.current) return;

        const bridge = bridgeRef.current;

        const meeting = meetingRef.current;

        if (meeting && meeting?.state !== MeetingState.STOPPED) {
          bridge?.postMeeting(meeting);
        } else {
          bridge?.postMeeting(undefined);
        }
      },
      100,
      { maxWait: 300 },
    ),
    [],
  );

  const broadcastSession = useCallback(
    debounce(
      () => {
        // the debounce can cause this fn to be called after unmount
        // which overrides the undefined posting on unmount
        if (unmountedRef.current) return;

        const bridge = bridgeRef.current;

        const meeting = meetingRef.current;
        const session = sessionRef.current;

        if (meeting && meeting?.state !== MeetingState.STOPPED) {
          bridge?.postSession(session);
        } else {
          bridge?.postSession(undefined);
        }
      },
      100,
      { maxWait: 300 },
    ),
    [],
  );

  const broadcastSessions = useCallback(
    debounce(
      () => {
        // the debounce can cause this fn to be called after unmount
        // which overrides the undefined posting on unmount
        if (unmountedRef.current) return;

        const bridge = bridgeRef.current;

        const meeting = meetingRef.current;
        const sessions = sessionsRef.current;

        if (meeting && meeting?.state !== MeetingState.STOPPED) {
          bridge?.postSessions(sessions);
        } else {
          bridge?.postSessions(undefined);
        }
      },
      100,
      { maxWait: 300 },
    ),
    [],
  );

  const broadcastChatrooms = useCallback(
    debounce(
      () => {
        // the debounce can cause this fn to be called after unmount
        // which overrides the undefined posting on unmount
        if (unmountedRef.current) return;

        const bridge = bridgeRef.current;

        const meeting = meetingRef.current;
        const chatrooms = chatroomsRef.current;

        if (meeting && meeting?.state !== MeetingState.STOPPED) {
          bridge?.postChatrooms(chatrooms);
        } else {
          bridge?.postChatrooms(undefined);
        }
      },
      100,
      { maxWait: 300 },
    ),
    [],
  );

  const broadcastChat = useCallback(
    debounce(
      () => {
        if (unmountedRef.current) return;
        const bridge = bridgeRef.current;
        if (!bridge) return;
        const adapter = chatAdapterRef.current;
        if (!adapter) return;
        const chatroom = chatroomsRef.current?.find((c) => c.id === adapter.getChatroomId());
        if (!chatroom) return;
        bridge.postChat(chatroom.id, adapter.getChatroomState() ?? chatroom.status, adapter.getMessages());
      },
      100,
      { maxWait: 300 },
    ),
    [],
  );

  const CarouselAPI = useVideoCarousel({
    meetingAPI: containerAPI,
    getClients: getMessagingBridgeClients,
    onCarousel: broadcastVideoCarousel,
  });
  const CarouselAPIRef = useRefAutoUpdate(CarouselAPI); 

  useEffect(() => {
    const onUnload = () => {
      bridgeRef.current?.postInitData(undefined, undefined, undefined, undefined);
      CarouselAPIRef.current?.stop();
      unmountedRef.current = true;
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      onUnload();
    };
  }, []);

  useEffect(() => {
    if (!bridge) return;
    const getMeetingListener = bridge.addEventListener("requestInitData", (_, respond) => {
      respond(meetingRef.current, sessionRef.current, sessionsRef.current, chatroomsRef.current);
    });

    const getChatListener = bridge.addEventListener("requestChat", (_, respond) => {
      const adapter = chatAdapterRef.current;
      if (!adapter) return;
      const chatroom = chatroomsRef.current?.find((c) => c.id === adapter.getChatroomId());
      if (!chatroom) return;
      respond(chatroom.id, adapter.getChatroomState() ?? chatroom.status, adapter.getMessages());
    });

    const clientDataListener = bridge.addEventListener("clientChange", () => {
      const newClients = getMessagingBridgeClients();
      if (newClients.length) {
        if (!CarouselAPI.isRunning()) CarouselAPI.start();
        else CarouselAPI.update();
      } else if (!newClients.length) {
        CarouselAPI.stop();
      }
    });

    return () => {
      getMeetingListener.flush();
      getChatListener.flush();
      clientDataListener.flush();
    };
  }, [bridge, CarouselAPI]);

  /////////////////////////
  ////// Broadcasting /////
  /////////////////////////

  // meeting?.state: broadcasts check meeting state to decide whether to send data or undefined

  useEffect(() => {
    broadcastMeeting();
  }, [meeting]);

  useEffect(() => {
    broadcastSession();
  }, [session, meeting?.state]);

  useEffect(() => {
    broadcastSessions();
  }, [sessions, meeting?.state]);

  useEffect(() => {
    broadcastChatrooms();
  }, [chatrooms, meeting?.state]);

  useEffect(() => {
    const chatCleanMessagesListener = chatAdapter?.addEventListener("cleanMessages", () => {
      broadcastChat();
    });
    const chatNewMessagesListener = chatAdapter?.addEventListener("newMessages", () => broadcastChat());
    const chatUpdateMessageListener = chatAdapter?.addEventListener("updateMessage", () => broadcastChat());
    const chatUpdateManyMessagesListener = chatAdapter?.addEventListener("updateMessages", () => broadcastChat());
    const chatStateListener = chatAdapter?.addEventListener("state", () => broadcastChat());
    return () => {
      chatCleanMessagesListener?.flush();
      chatNewMessagesListener?.flush();
      chatUpdateMessageListener?.flush();
      chatUpdateManyMessagesListener?.flush();
      chatStateListener?.flush();
    };
  }, [chatAdapter, broadcastChat]);

  useEffect(() => {
    if (!bridge) return;
    bridge.postTheme(themeCtx.preferredTheme);
  }, [bridge, themeCtx]);

  return bridge;
};

export const useMeetingSlaveMessagingBridge = () => {
  const bridge = useMessagingBridgeManagerSlaveContext();
  const bridgeRef = useRefAutoUpdate(bridge);

  const [meeting, setMeeting] = useState<Meeting>();
  const [session, setSession] = useState<MeetingSession>();
  const [sessions, setSessions] = useState<MeetingSession[]>();
  const [chatrooms, setChatrooms] = useState<Chatroom[]>();

  const [qrCodeData, setQRCodeData] = useState<CyclingQRCodeData>();
  const currentChatroomId = useRef<string>();
  const themeCtx = useThemeContext();
  const themeCtxRef = useRefAutoUpdate(themeCtx);

  const slaveChatAdapter = useRef<SlaveChatAdapter>(new SlaveChatAdapter(true, []));

  useEffect(() => {
    if (!bridge) return;

    const listeners: AddEventListernerResult<any, any>[] = [];

    listeners.push(
      bridge.addEventListener("ready", () => {
        bridge.postRequestInitData();
      })
    );

    listeners.push(
      bridge.addEventListener("initData", (msg) => {
        const { meeting, session, sessions, chatrooms } = msg.content;
        setMeeting(meeting);
        setSession(session);
        setSessions(sessions);
        setChatrooms(chatrooms);
      })
    );

    listeners.push(
      bridge.addEventListener("meeting", (msg) => {
        setMeeting(msg.content);
      })
    );

    listeners.push(
      bridge.addEventListener("session", (msg) => {
        setSession(msg.content);
      })
    );

    listeners.push(
      bridge.addEventListener("sessions", (msg) => {
        setSessions(msg.content);
      })
    );

    listeners.push(
      bridge.addEventListener("chatrooms", (msg) => {
        setChatrooms(msg.content);
      })
    );

    listeners.push(
      bridge.addEventListener("chat", (msg) => {
        const { state, messages } = msg.content;
        if (state === ChatroomStatus.disabled) slaveChatAdapter.current.disable();
        else slaveChatAdapter.current.enable();
        slaveChatAdapter.current.updateMessages(
          messages.map((m) => {
            m.date = new Date(m.date); // date becomes string through the websocket
            return m;
          })
        );
      })
    );

    listeners.push(
      bridge.addEventListener("qrCodeData", (msg) => {
        setQRCodeData(msg.content.data);
      })
    );

    listeners.push(
      bridge.addEventListener("theme", (msg) => {
        themeCtxRef.current.setPreferredTheme(msg.content);
      })
    );

    listeners.push(
      bridge.addEventListener("disconnect", () => {
        setMeeting(undefined);
        setSession(undefined);
        setSessions(undefined);
        setChatrooms(undefined);
        setQRCodeData(undefined);
        currentChatroomId.current = undefined;
        bridge.postRequestInitData();
      })
    );

    bridge.postRequestInitData();

    return () => listeners.forEach(l => l.flush());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridge]);

  useEffect(() => {
    const chatroomId = chatrooms?.[0]?.id;
    if (chatroomId && chatroomId !== currentChatroomId.current) {
      bridgeRef.current?.postRequestChat(chatroomId);
    }
    currentChatroomId.current = chatroomId;
  }, [chatrooms]);

  return { bridge, meeting, session, sessions, qrCodeData, slaveChatAdapter: slaveChatAdapter.current };
};
